(ns frontend.ui.page.lp-report-editor.step-1-initialization.sub
  (:require
   [re-frame.core :as rf]
   [common.eid :as eid]
   [common.localization :as loc]
   [common.lp-portal-import-errors :as import-errors]
   [common.schema.entities.lp-portal.lp-report :as lp-report]
   [frontend.ui.page.lp-report-editor.sub :as editor-sub]
   [common.util :as cu]
   [common.schema.entities.lp-portal.lp-report-holding :as lp-report-holding]))

(def initialization-path [:lp-report-editor :initialization])
(def delete-dialog-path (conj initialization-path :delete-dialog-visible?))
(def template-change-dialog-path (conj initialization-path :change-template-dialog-visible?))
(def delete-dialog-processing-path (conj initialization-path :delete-processing?))
(def collapse-path (conj initialization-path :step-collapsed?))
(def edit-path (conj initialization-path :step-edit?))
(def fund-file-status-path (conj initialization-path :fund-file-status))
(def fund-file-uploading-path (conj initialization-path :uploading))
(def feeder-config-path (conj initialization-path :feeders))
(def holdings-config-path (conj initialization-path :holdings-config))
(def holdings-processing-path (conj initialization-path :holdings-processing?))
(def feeders-processing-path (conj initialization-path :feeders-processing?))
(def basic-config-path (conj initialization-path :basic-config))

(rf/reg-sub
 ::holdings-processing?
 (fn [db]
   (get-in db holdings-processing-path)))

(rf/reg-sub
 ::change-template-dialog-visible?
 (fn [db]
   (get-in db template-change-dialog-path)))

(rf/reg-sub
 ::can-disable-cycle?
 (fn [db]
   (let [report (get-in db editor-sub/report-path)
         admin? (get-in db editor-sub/reporting-admin?-path)
         owner? (some #{(get-in db editor-sub/account-id-path)}
                      (:lp-report/process-owners report))]
     (or admin?
         owner?))))

(rf/reg-sub
 ::can-begin-cycle?
 (fn [db]
   (let [report (get-in db editor-sub/report-path)]
     (and (= :lp-report.step/initialization (:lp-report/step report))
          (:lp-report/template-type report)
          (seq (:lp-report/process-owners report))
          (seq (:lp-report/text-contributors report))
          (seq (:lp-report/data-contributors report))
          (:lp-report/final-approver report)
          (:lp-report/text-approver report)
          (:lp-report/data-approver report)
          (:lp-report/title report)
          (:lp-report/report-data-file report)
          (not (get-in db (concat edit-path [:step-1])))
          (not (get-in db (concat edit-path [:step-3])))
          (->> report
               :lp-report/holdings
               (every? (fn [id]
                         (let [holding (get-in db (conj editor-sub/holdings-path id))]
                           (:lp-report-holding/template-type holding)))))))))

(rf/reg-sub
 ::delete-dialog-visible?
 (fn [db]
   (get-in db delete-dialog-path)))

(rf/reg-sub
 ::delete-dialog-processing?
 (fn [db]
   (get-in db delete-dialog-processing-path)))

(rf/reg-sub
 ::collapsed?
 (fn [db [_ step]]
   (get-in db (concat collapse-path [step]))))

(rf/reg-sub
 ::step-disabled?
 (fn [db [_ step]]
   (let [report (get-in db editor-sub/report-path)
         prerequisites-done? (case step
                               :step-1 true
                               :step-2 (:lp-report/template-type report)
                               :step-3 (:lp-report/holdings report)
                               :step-4 (:lp-report/holdings report)
                               false)]
     (not prerequisites-done?))))

(rf/reg-sub
 ::step-complete?
 (fn [db [_ step]]
   (let [report (get-in db editor-sub/report-path)
         step-completed?
         (case step
           :step-1 (and (:lp-report/template-type report)
                        (seq (:lp-report/process-owners report))
                        (seq (:lp-report/text-contributors report))
                        (seq (:lp-report/data-contributors report))
                        (:lp-report/final-approver report)
                        (:lp-report/text-approver report)
                        (:lp-report/data-approver report)
                        (not (cu/blank-value? (:lp-report/title report))))
           :step-2 (= :processing.status/complete
                      (->> report
                           :lp-report/report-data-status
                           (editor-sub/parse-data-status)
                           :processing-status))
           :step-3
           (let [holdings (:lp-report/holdings report)]
             (and
              (seq holdings)
              (->> holdings
                   (every?
                    (fn [holding-id]
                      (let [holding (get-in db (conj editor-sub/holdings-path holding-id))]
                        (or (= :lp-report-holding-template/no-reporting
                               (:lp-report-holding/template-type holding))
                            (and (:lp-report-holding/text-approver holding)
                                 (:lp-report-holding/data-approver holding)
                                 (seq (:lp-report-holding/text-contributors holding))
                                 (seq (:lp-report-holding/data-contributors holding))
                                 (:lp-report-holding/template-type holding)
                                 (not (cu/blank-value? (:lp-report-holding/name holding)))))))))))
           :step-4 (:lp-report/investors report)
           false)]
     step-completed?)))

(rf/reg-sub
 ::can-edit?
 (fn [db [_ step]]
   (let [report (get-in db editor-sub/report-path)
         admin? (get-in db editor-sub/reporting-admin?-path)
         owner? (some #{(get-in db editor-sub/account-id-path)}
                      (:lp-report/process-owners report))
         edit-permissions? (lp-report/can-configure? report (or owner? admin?))
         prerequisites-done? (case step
                               :step-1 true
                               :step-2 (:lp-report/template-type report)
                               :step-3 (:lp-report/holdings report)
                               :step-4 (:lp-report/holdings report)
                               false)]
     (and edit-permissions?
          prerequisites-done?))))

(rf/reg-sub
 ::edit-mode?
 (fn [db [_ step]]
   (let [default false]
     (get-in db (concat edit-path [step]) default))))

(rf/reg-sub
 ::basic-config
 (fn [db _]
   (get-in db basic-config-path)))

(rf/reg-sub
 ::feeder-configs
 (fn [db _]
   (let [feeder-ids (get-in db [:lp-report-editor :lp-report :lp-report/feeder-fund-reports])]
     (->> feeder-ids
          (map (fn [fid]
                 (get-in db [:lp-report-editor :feeder-reports fid])))))))

(rf/reg-sub
 ::holding-edits
 (fn [db _]
   (get-in db holdings-config-path)))

(rf/reg-sub
 ::holdings
 (fn [db [_ step]]
   (let [edit-mode? (get-in db (concat edit-path [step]))]
     (->> (if edit-mode?
            (vals (get-in db holdings-config-path))
            (let [report (get-in db editor-sub/report-path)]
              (->> report
                   :lp-report/holdings
                   (map (fn [fid]
                          (get-in db (conj editor-sub/holdings-path fid)))))))
          (sort lp-report-holding/compare-holdings)
          (vec)))))

(rf/reg-sub
 ::feeder-edits
 (fn [db _]
   (get-in db feeder-config-path)))

(rf/reg-sub
 ::feeders
 (fn [db [_ step]]
   (let [edit-mode? (get-in db (concat edit-path [step]))]
     (if edit-mode?
       (vals (get-in db feeder-config-path))
       (get-in db editor-sub/feeder-reports-path)))))

(rf/reg-sub
 ::fund-file-status
 (fn [db _]
   (let [;; Sent as {:db/id n}; normalize before keying the attachment map.
         report-file-id (-> (get-in db [:lp-report-editor :lp-report :lp-report/report-data-file])
                            (eid/eid))
         ;; text-edn field: arrives as a pr-str string.
         report-status (-> (get-in db [:lp-report-editor :lp-report :lp-report/report-data-status])
                           (editor-sub/parse-data-status))
         attachment (get-in db [:lp-report-editor :lp-report-attachments report-file-id])
         uploading (get-in db fund-file-uploading-path)
         {:keys [processing-status progress progress-message-kw message file-name
                 formula-errors]} report-status
         ;; A failed import elsewhere does not replace the report's data file,
         ;; so a status carrying a different file describes that attempt, not
         ;; the file shown here. Report the file as it stands instead.
         status-describes-current-file? (or (nil? (:file-id report-status))
                                            (= (:file-id report-status)
                                               report-file-id))
         status (cond
                  (and (not= :status/uploading processing-status) 
                       (or (not report-file-id)
                           (not attachment)
                           (:common/disabled? attachment)))
                  :status/no-file

                  uploading
                  :status/uploading

                  (not status-describes-current-file?)
                  :processing.status/complete

                  :else
                  processing-status)]
     {:file-id   report-file-id
      :file-name (or (:attachment/filename attachment) file-name)
      :status    status
      :progress  (case status
                   :status/no-file 0
                   :status/uploading progress
                   :processing.status/complete 100
                   :processing.status/processing progress
                   :processing.status/error progress
                   :processing.status/cancelled 0
                   0)
      ;; Detail goes to the alert, not the bar.
      :error-detail (when (= :processing.status/error status)
                      (or
                       ;; Localized here, in the viewer's language.
                       (import-errors/formula-errors-message formula-errors)
                       message
                       ;; Fallback when the backend sent no detail.
                       (when progress-message-kw
                         (loc/tr progress-message-kw))))
      :formula-errors (when (= :processing.status/error status)
                        formula-errors)
      ;; Bar shows a short label only.
      :message   (or
                  ;; The stored message belongs to the attempt that wrote it,
                  ;; so a status describing another file must not lend its
                  ;; wording to the file shown here.
                  (when (and progress-message-kw
                             status-describes-current-file?
                             (not= :processing.status/error status))
                    (loc/tr progress-message-kw))
                  (case status
                    :status/no-file (loc/tr :no-file)
                    :status/uploading (loc/tr :processing)
                    :processing.status/complete (loc/tr :processing.status/complete)
                    :processing.status/processing (loc/tr :progress-percentage
                                                          [(int (* 100 (or progress 0)))])
                    :processing.status/error (loc/tr :processing.status/error)
                    :processing.status/cancelled (loc/tr :processing.status/cancelled)
                    (str status)))})))
